import React from "react";
import { AreaChart, AreaChartPoint, DonutChart, DonutSlice } from "../InlineCharts";

// Shared presentation primitives for the Assessments and Attendance reports.
// These are visual wrappers around the existing InlineCharts SVG components
// — data and calculations remain 100% owned by the caller. No assessment
// pass/fail logic here, no attendance formulas here, no coupling.
//
// The Attendance report can consume the same ChartCard shell + TrendCard
// + DistributionCard by passing its own points/slices and its own tooltip
// renderer. If it doesn't need the target line, it just omits the prop.

// ── ChartCard: the standard chart-panel chrome ──
export interface ChartCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Minimum body height; helps keep sparse states from collapsing. */
  minBodyHeight?: number;
}
export const ChartCard: React.FC<ChartCardProps> = ({
  title, subtitle, icon, action, children, minBodyHeight,
}) => (
  <div style={{
    background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
    padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    display: "flex", flexDirection: "column",
  }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1b3a6b", display: "flex", alignItems: "center", gap: 6 }}>
          {icon}
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
    <div style={{ minHeight: minBodyHeight ?? 0, display: "flex", flexDirection: "column" }}>
      {children}
    </div>
  </div>
);

// ── ChartEmpty: the standard sparse/empty state shown inside a ChartCard ──
export interface ChartEmptyProps {
  icon?: React.ReactNode;
  title: string;
  hint?: string;
}
export const ChartEmpty: React.FC<ChartEmptyProps> = ({ icon = "📊", title, hint }) => (
  <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
    <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "#1b3a6b" }}>{title}</div>
    {hint && <div style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
  </div>
);

// ── TrendCard: title + AreaChart + optional footer strip ──
export interface TrendCardProps extends Omit<ChartCardProps, "children"> {
  data: AreaChartPoint[];
  color?: string;
  gradientId?: string;
  target?: number;
  targetLabel?: string;
  height?: number;
  renderTooltip?: (p: AreaChartPoint) => React.ReactNode;
  dotColorFor?: ((v: number) => string) | null;
  singlePointEmphasis?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  footer?: React.ReactNode;
}
export const TrendCard: React.FC<TrendCardProps> = ({
  data, color = "#7c3aed", gradientId = "trendGrad",
  target, targetLabel, height = 240, renderTooltip, dotColorFor,
  singlePointEmphasis,
  emptyTitle = "No data yet", emptyHint,
  footer,
  ...cardProps
}) => (
  <ChartCard {...cardProps}>
    {data.length === 0 ? (
      <ChartEmpty title={emptyTitle} hint={emptyHint} icon="📝" />
    ) : (
      <>
        <AreaChart
          data={data} height={height}
          color={color} gradientId={gradientId}
          target={target} targetLabel={targetLabel}
          renderTooltip={renderTooltip}
          dotColorFor={dotColorFor}
          singlePointEmphasis={singlePointEmphasis}
        />
        {footer && (
          <div style={{
            display: "flex", gap: 12, marginTop: 10,
            padding: "0 8px", flexWrap: "wrap", alignItems: "center",
          }}>{footer}</div>
        )}
      </>
    )}
  </ChartCard>
);

// ── DistributionCard: title + Donut + legend ──
export interface DistributionCardProps extends Omit<ChartCardProps, "children"> {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  centerLabel?: string | number;
  centerSub?: string;
  renderTooltip?: (s: DonutSlice) => React.ReactNode;
  legend?: React.ReactNode;
  emptyTitle?: string;
  emptyHint?: string;
}
export const DistributionCard: React.FC<DistributionCardProps> = ({
  slices, size = 180, thickness = 26,
  centerLabel, centerSub,
  renderTooltip, legend,
  emptyTitle = "No data yet", emptyHint,
  ...cardProps
}) => {
  const total = slices.reduce((s, x) => s + x.value, 0);
  return (
    <ChartCard {...cardProps}>
      {total === 0 ? (
        <ChartEmpty title={emptyTitle} hint={emptyHint} icon="🎯" />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "center", position: "relative", minHeight: size }}>
            <DonutChart
              size={size} thickness={thickness}
              slices={slices}
              centerLabel={String(centerLabel ?? total)}
              centerSub={centerSub ?? "Total"}
              renderTooltip={renderTooltip}
            />
          </div>
          {legend}
        </>
      )}
    </ChartCard>
  );
};
